import { CartOrderForm } from '@b2b/types/cart';
import { CartEventOrderForm } from '@b2b/types/event';
import { CartMembershipOrderForm } from '@b2b/types/membership';
import { Membership } from '@b2b/types/membership';
import { SalesChannelType } from '@b2b/types/core';

/**
 * Filter event forms by sales channel preference
 * Uses the 'preferences' object on event forms
 */
export function filterEventFormsBySalesChannel(
  forms: CartEventOrderForm[],
  salesChannel: string
): CartEventOrderForm[] {
  return forms.filter((form) => {
    // Check if the form has preferences for the specified sales channel
    if (form.preferences?.[salesChannel]) {
      const preference = form.preferences[salesChannel];
      return preference === 'displayed' || preference === 'required';
    }
    return false;
  });
}

/**
 * Case-insensitive sales channel comparison.
 * The API returns saleChannel in PascalCase (e.g. "InsideSalesIndividual")
 * while the code uses camelCase (e.g. "insideSalesIndividual").
 */
function isSameChannel(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Filter membership forms by sales channel and membership purchase type
 * 
 * Logic:
 * 1. Check if form applies to the membership purchase type (gift vs standard)
 * 2. Check if form is enabled for the sales channel (displayForm must be true)
 * 3. Return filtered forms
 * 
 * @param forms - Array of membership forms
 * @param memberships - Array of membership items in cart
 * @param salesChannel - The sales channel to filter by (e.g., 'insideSalesIndividual')
 */
export function filterMembershipFormsBySalesChannel(
  forms: CartMembershipOrderForm[],
  memberships: Membership[],
  salesChannel: SalesChannelType
): CartMembershipOrderForm[] {
  return forms.filter((form) => {
    // Step 1: Check if form applies to ANY membership in cart based on purchase type
    const appliesToAnyMembership = memberships.some((membership) => {
      const isGift = membership.membershipInfo?.isGift ?? false;
      
      if (isGift) {
        // For gift memberships: form must have displayFormForGiftPurchase = true
        return form.displayFormForGiftPurchase === true;
      } else {
        // For standard (non-gift) memberships: form must have displayFormForStandardPurchase = true
        return form.displayFormForStandardPurchase === true;
      }
    });

    if (!appliesToAnyMembership) {
      return false; // Form doesn't apply to any membership in cart
    }

    // Step 2: Check sales channel restriction
    const channelRestriction = form.formPreferences?.find(
      (pref) => isSameChannel(pref.saleChannel, salesChannel)
    );

    if (!channelRestriction) {
      return false; // No restriction found for this channel
    }

    if (!channelRestriction.displayForm) {
      return false; // Form is explicitly disabled for this channel
    }

    // ✅ Form passed all checks
    return true;
  });
}

/**
 * Determine if a form is required based on sales channel
 */
export function isFormRequired(
  form: CartOrderForm,
  salesChannel: string
): boolean {
  // Check event forms (use preferences)
  if ('preferences' in form && form.preferences?.[salesChannel]) {
    return form.preferences[salesChannel] === 'required';
  }

  // Check membership forms (use formPreferences)
  if ('formPreferences' in form && form.formPreferences) {
    const preference = form.formPreferences.find((p) => isSameChannel(p.saleChannel, salesChannel));
    return preference?.formResponseRequired ?? false;
  }

  return false;
}

/**
 * Returns the display name for a form's associated item (event name or membership name).
 */
function getItemName(form: CartOrderForm): string {
  if ('event' in form && (form as CartEventOrderForm).event) {
    const event = (form as CartEventOrderForm).event;
    const datePart = event.displayStartDate
      ? ` - ${event.displayStartDate} from ${event.displayStartTime} - ${event.displayEndTime}`
      : '';
    return `${event.name}${datePart}`;
  }
  if ('membershipName' in form) {
    return (form as CartMembershipOrderForm).membershipName;
  }
  return '';
}

/**
 * Deduplicate forms based on formDisplaySettings.
 * Each returned form includes a `relatedItemNames` array:
 * - `perCart` forms: all item names sharing that form (>1 means "Multiple")
 * - `perItem` / other forms: the single item name for that row
 *
 * @param forms - Array of forms to deduplicate
 * @returns Deduplicated array of forms with relatedItemNames
 */
export function deduplicateFormsByDisplaySettings(
  forms: CartOrderForm[]
): (CartOrderForm & { relatedItemNames: string[] })[] {
  // First pass: collect all unique item names for each perCart form ID
  const itemNamesByFormId = new Map<string, string[]>();
  forms.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      const name = getItemName(form);
      if (!itemNamesByFormId.has(form.id)) {
        itemNamesByFormId.set(form.id, []);
      }
      const names = itemNamesByFormId.get(form.id)!;
      if (name && !names.includes(name)) {
        names.push(name);
      }
    }
  });

  const seen = new Map<string, CartOrderForm & { relatedItemNames: string[] }>();

  forms.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      // Only keep first occurrence; attach all related item names
      if (!seen.has(form.id)) {
        const relatedItemNames = itemNamesByFormId.get(form.id) ?? [getItemName(form)];
        seen.set(form.id, { ...form, relatedItemNames });
      } else {
        // Already added this form ID; if this occurrence is required, propagate that to the existing entry
        const existing = seen.get(form.id);
        const isRequired = isFormRequired(form, form.itemType === 'event' ? 'insideSales' : 'insideSalesIndividual');

        if (existing && isRequired) {
          if (form.itemType === 'event') {
            const existingEvent = existing as CartEventOrderForm & { relatedItemNames: string[] };
            existingEvent.preferences = {
              ...existingEvent.preferences,
              insideSales: form.preferences?.insideSales,
            };
            seen.set(form.id, { ...existingEvent });
          } else {
            const existingMembership = existing as CartMembershipOrderForm & { relatedItemNames: string[] };
            existingMembership.formPreferences = (existingMembership.formPreferences ?? []).map((pref) =>
              isSameChannel(pref.saleChannel, 'insideSalesIndividual')
                ? { ...pref, formResponseRequired: true }
                : pref
            );
            seen.set(form.id, { ...existingMembership });
          }
        }
      }
    } else if (form.formDisplaySettings === 'perItem') {
      // Keep all occurrences with their own item name
      const key = `${form.id}-${seen.size}`;
      seen.set(key, { ...form, relatedItemNames: [getItemName(form)] });
    } else {
      seen.set(form.id, { ...form, relatedItemNames: [getItemName(form)] });
    }
  });

  return Array.from(seen.values());
}